/**
 * The hold surface's palette, in one place so the composer and the hold cards
 * cannot drift apart.
 *
 * This is a deliberate departure from the teal used elsewhere in Inquiries:
 * the hold flow was redesigned around a green/tan/red set where each colour
 * carries a single meaning —
 *
 *   GREEN  a choice you made, and time behaving normally (primary buttons,
 *          toggles that are on, the ELAPSED figure while it is still inside
 *          the requested window)
 *   TAN    held, and nothing more (the selected portion of day, the ON HOLD
 *          badge). Tan already meant "on hold" in the calendar and on the
 *          schedule board, so it is carried over rather than invented.
 *   RED    past due, or destructive (the Release link, overdue text and badge)
 *
 * The pre-existing amber family (#fbf1dd / #e3c489 / #B45309) is still what
 * the calendar and StaticBoard paint held days with; TAN_* below is the same
 * idea one step warmer for solid fills. Both are kept — repainting the
 * schedule board is not part of this change.
 */

export const FONT = 'Inter, sans-serif'
export const FONT_DISPLAY = "'Inter Tight', sans-serif"

/* Primary — dark green */
export const GREEN = '#14532D'
export const GREEN_DEEP = '#1F3D2F'
export const GREEN_HOVER = '#1a6438'

/* Held — the amber family the calendar and StaticBoard ALREADY paint held days
 * with (#fbf1dd / #e3c489 / #B45309), rather than the slightly warmer TAN_*
 * set this file used to carry. Two surfaces painting "held" two different
 * colours is drift; this closes it from the modal's side.
 *
 * #E3C489 is the family's BORDER, not its fill. Used as a solid fill it reads
 * as a heavy mustard slab; used as a ring on the light track it reads as
 * "held" and matches a held day in the calendar exactly. TAN_FILL is kept at
 * the same value so anything still filling with it stays in the family. */
export const TAN_FILL = '#E3C489'
export const TAN_BORDER = '#E3C489'
export const TAN_TRACK = '#FBF1DD'
export const TAN_INK = '#4A3B16'   /* 6.5:1 on the fill, 9.7:1 on the track */

/* Overdue / destructive — red */
export const RED = '#B91C1C'
export const RED_FILL = '#F6E2E0'
export const RED_BORDER = '#EFC9C6'

/* Neutrals */
export const MUTED = '#8A8A82'
export const INK = '#1A1A17'
export const INK_SOFT = '#4B4B45'
export const HAIRLINE = '#E7E5DF'
export const BORDER_LIGHT = '#E2E1DC'
export const CHIP_BG = '#F3F3F0'
export const TRACK_OFF = '#D8D7D1'

/* The card shell every hold surface sits in: white, ~16px radius, hairline
 * border, soft shadow, generous padding. */
export const cardStyle = {
    background: '#fff',
    borderRadius: 16,
    border: `1px solid ${HAIRLINE}`,
    boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(16,24,40,0.06)',
    fontFamily: FONT,
}

/* UPPERCASE, ~11px, letter-spaced, muted — every section label on the hold
 * surface, so they are all the same label. */
export const sectionLabelStyle = {
    display: 'block',
    fontFamily: FONT,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: MUTED,
    lineHeight: 1.2,
}
